import { v4 as uuidv4 } from 'uuid';
import { data_hub_privacy_policy_allows_browser_storage } from '../helpers/data_hub_privacy_policy';
import { build_page_metadata } from './metadata';
import {
    sanitize_event_name,
    sanitize_structured_event_payload
} from './schema';

const session_storage_key_prefix = 'data-hub:web:session-id';

function get_session_storage_key(data_hub_instance) {
    const organization_id = typeof data_hub_instance?.organization_id === 'string' && data_hub_instance.organization_id.trim()
        ? data_hub_instance.organization_id.trim()
        : 'shared';
    return `${session_storage_key_prefix}:${organization_id}`;
}

function read_session_id_from_storage(data_hub_instance) {
    if (!data_hub_privacy_policy_allows_browser_storage(data_hub_instance)) {
        return null;
    }
    if (typeof window === 'undefined' || !window.sessionStorage) {
        return null;
    }
    try {
        return window.sessionStorage.getItem(get_session_storage_key(data_hub_instance)) || null;
    } catch {
        return null;
    }
}

function write_session_id_to_storage(data_hub_instance, session_id) {
    if (!session_id || !data_hub_privacy_policy_allows_browser_storage(data_hub_instance)) {
        return;
    }
    if (typeof window === 'undefined' || !window.sessionStorage) {
        return;
    }
    try {
        window.sessionStorage.setItem(get_session_storage_key(data_hub_instance), session_id);
    } catch {
        // Storage may be blocked in private or restricted contexts.
    }
}

/**
 * Ensures analytics identifiers exist for the current DataHub instance. session_id is stored
 * in sessionStorage when privacy settings allow browser storage. window_id is for this loaded
 * document. pageview_id is rotated for single-page app route changes.
 *
 * @param {Object} data_hub_instance - DataHub instance or compatible object.
 * @returns {Object|null} Analytics state object, or null for invalid instances.
 */
export function initialize_analytics_session(data_hub_instance) {
    if (!data_hub_instance || typeof data_hub_instance !== 'object') {
        return null;
    }

    const existing_state = data_hub_instance.analytics_state || {};
    data_hub_instance.page_metadata = data_hub_instance.page_metadata && typeof data_hub_instance.page_metadata === 'object'
        ? data_hub_instance.page_metadata
        : {};

    const session_id = existing_state.session_id
        || read_session_id_from_storage(data_hub_instance)
        || uuidv4();
    const window_id = existing_state.window_id || uuidv4();
    // Adopt an existing pageview_id (set by data_hub_metadata.set_page_metadata_defaults in
    // the constructor before analytics bootstrap) so analytics events and actblue refcodes
    // share the same UUID for the initial pageview.
    const pageview_id = existing_state.pageview_id
        || data_hub_instance.page_metadata.pageview_id
        || uuidv4();

    write_session_id_to_storage(data_hub_instance, session_id);

    data_hub_instance.page_metadata.session_id = session_id;
    data_hub_instance.page_metadata.window_id = window_id;
    data_hub_instance.page_metadata.pageview_id = pageview_id;

    data_hub_instance.analytics_state = {
        ...existing_state,
        session_id,
        window_id,
        pageview_id,
        session_strategy: existing_state.session_strategy || 'tab',
        event_sequence: Number.isFinite(existing_state.event_sequence) ? existing_state.event_sequence : 0
    };

    return data_hub_instance.analytics_state;
}

/**
 * Rotates the logical pageview id for single-page app route changes while keeping the same
 * session and window identifiers. This should be called only after the route has changed.
 * Both page_metadata.pageview_id rotate to the new value
 * so analytics events and any subsequent actblue donation refcodes (refcodePageviewId /
 * refcodePageviewUuid) tie back to the page the user was actually on at the time.
 *
 * @param {Object} data_hub_instance - DataHub instance or compatible object.
 * @returns {string|null} New pageview id, or null when the instance is invalid.
 */
export function rotate_pageview_id(data_hub_instance) {
    const analytics_state = initialize_analytics_session(data_hub_instance);
    if (!analytics_state) {
        return null;
    }
    const pageview_id = uuidv4();
    analytics_state.pageview_id = pageview_id;
    analytics_state.page_leave_sent_by_pageview_id = analytics_state.page_leave_sent_by_pageview_id || new Set();
    data_hub_instance.page_metadata = data_hub_instance.page_metadata || {};
    data_hub_instance.page_metadata.pageview_id = pageview_id;
    return pageview_id;
}

export function get_next_event_sequence(data_hub_instance) {
    const analytics_state = data_hub_instance?.analytics_state || initialize_analytics_session(data_hub_instance);
    if (!analytics_state) {
        return null;
    }
    analytics_state.event_sequence = (analytics_state.event_sequence || 0) + 1;
    return analytics_state.event_sequence;
}

/**
 * Snapshots full page metadata at capture time (pathname, UTM, title, SPA flags, etc.) so each
 * event carries the page context where it fired. Without this, only page_url is stamped and
 * pathname/UTM rely on batch-level metadata from the first queued event — wrong for SPA
 * pageviews flushed with older events and for Iterable mirrors that merge per-event metadata.
 * Caller-supplied metadata keys (e.g. is_spa_navigation) win via build_page_metadata merge order.
 */
function snapshot_page_metadata_at_capture(data_hub_instance, event_properties) {
    const caller_metadata = (event_properties && event_properties.metadata) || {};
    return {
        ...(event_properties || {}),
        metadata: build_page_metadata(data_hub_instance, caller_metadata)
    };
}

/**
 * Builds a single normalized analytics event with v1 identifier fields and sanitized optional
 * sections. This does not enqueue or deliver the event; callers decide where it goes.
 *
 * @param {Object} data_hub_instance - DataHub instance or compatible object.
 * @param {string} event_name - Event name such as $pageview, $click, or $video.
 * @param {Object} event_properties - Optional structured event properties.
 * @returns {Object|null} Normalized analytics event, or null if invalid.
 */
export function build_base_analytics_event(data_hub_instance, event_name, event_properties = {}) {
    try {
        const analytics_state = initialize_analytics_session(data_hub_instance);
        if (!analytics_state) {
            return null;
        }
        const sanitized_event_name = sanitize_event_name(event_name);
        if (!sanitized_event_name) {
            return null;
        }
        const sanitized_payload = sanitize_structured_event_payload(
            snapshot_page_metadata_at_capture(data_hub_instance, event_properties),
            data_hub_instance
        );
        const queued_at_ms = Date.now();
        const {
            metadata,
            browser,
            element,
            engagement,
            performance,
            video,
            commerce,
            web_event_data,
            event_sub_type
        } = sanitized_payload;

        const analytics_event = {
            event_name: sanitized_event_name,
            event_sub_type: event_sub_type || null,
            web_event_uuid: uuidv4(),
            session_id: analytics_state.session_id,
            window_id: analytics_state.window_id,
            pageview_id: analytics_state.pageview_id,
            session_strategy: analytics_state.session_strategy,
            event_sequence: get_next_event_sequence(data_hub_instance),
            client_time: new Date(queued_at_ms).toISOString(),
            client_timezone_offset_minutes: new Date().getTimezoneOffset(),
            client_queue_duration_ms: 0,
            metadata: metadata || undefined,
            browser: browser || undefined,
            element: element || undefined,
            engagement: engagement || undefined,
            performance: performance || undefined,
            video: video || undefined,
            commerce: commerce || undefined,
            web_event_data: web_event_data || undefined,
            __queued_at_ms: queued_at_ms
        };

        Object.keys(analytics_event).forEach((field_name) => {
            if (analytics_event[field_name] === undefined) {
                delete analytics_event[field_name];
            }
        });

        return analytics_event;
    } catch (error) {
        data_hub_instance?.error_function?.('Error building Data Hub analytics event: ' + error, null);
        return null;
    }
}